import type { BoardDoc } from '../doc/BoardDoc';
import { rectCenter, rectContains, rotatedBounds } from '../doc/geometry';
import type { FrameItem, Item } from '../doc/schema';

/** Does `item` belong to `frame`? Items by centre; nested frames only when fully inside. */
export function frameContains(frame: FrameItem, item: Item): boolean {
  if (item.id === frame.id) return false;
  const b = rotatedBounds(item);
  if (item.type === 'frame') return rectContains(frame, b);
  const c = rectCenter(b);
  return c.x >= frame.x && c.x <= frame.x + frame.w && c.y >= frame.y && c.y <= frame.y + frame.h;
}

/** Movable items inside the frame (hidden and locked ones stay where they are). */
export function frameContents(board: BoardDoc, frame: FrameItem): Item[] {
  return board.index.query(frame).filter((i) => !board.isLocked(i) && !board.isHidden(i) && frameContains(frame, i));
}

/**
 * Expand a set of items so every selected frame brings its contents along (recursively for
 * nested frames). Used by move, nudge and cross-pane drops.
 */
export function withFrameContents(board: BoardDoc, items: Item[]): Item[] {
  const out = new Map<string, Item>();
  const queue = [...items];
  while (queue.length) {
    const it = queue.pop()!;
    if (out.has(it.id)) continue;
    out.set(it.id, it);
    if (it.type === 'frame') for (const c of frameContents(board, it)) if (!out.has(c.id)) queue.push(c);
  }
  return [...out.values()];
}
